import * as React from "react";
import { useWalletClient } from "wagmi";
import { BrowserProvider, JsonRpcSigner } from "ethers";

/**
 * Convert a Viem wallet client to an ethers.js signer
 * @param {Object} walletClient - The Viem wallet client from Wagmi
 * @returns {JsonRpcSigner|undefined} The ethers.js signer
 */
export function walletClientToSigner(walletClient) {
  if (!walletClient) return undefined;

  const { account, transport } = walletClient;

  // Create a BrowserProvider with the wallet's transport
  const provider = new BrowserProvider(transport);

  // Create a JsonRpcSigner with the provider and account address
  const signer = new JsonRpcSigner(provider, account.address);

  return signer;
}

/**
 * Hook to get an ethers.js signer from Wagmi's wallet client
 * Converts the Viem wallet client to an ethers.js signer for backward compatibility
 * with contract interactions that expect ethers.js types
 * 
 * @param {Object} options - Options object
 * @param {number} options.chainId - Optional chain ID to get signer for specific chain
 * @returns {JsonRpcSigner|undefined} The ethers.js signer, or undefined if no wallet connected
 */
export function useEthersSigner({ chainId } = {}) {
  const { data: walletClient } = useWalletClient({ chainId });

  return React.useMemo(
    () => walletClientToSigner(walletClient),
    [walletClient]
  );
}
